import { adminApi, parseApiError } from './api';
import type { SystemSetting } from '@/types/admin.types';

// New API response format
interface SettingResponse {
  key: string;
  value: string;
  category: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  encrypted: boolean;
  description?: string;
  updatedAt: string;
  updatedBy?: string;
}

interface SettingsListResponse {
  settings: SettingResponse[];
  total: number;
}

interface StripeStatusResponse {
  configured: boolean;
  hasSecretKey: boolean;
  hasWebhookSecret: boolean;
  testMode?: boolean;
}

interface StripeConfigPayload {
  secretKey?: string;
  webhookSecret?: string;
}

// Convert API response to frontend type
const toSystemSetting = (s: SettingResponse): SystemSetting => ({
  key: s.key,
  value: s.value,
  valueType: s.type === 'number' ? 'number' : s.type === 'boolean' ? 'boolean' : 'string',
  category: s.category,
  description: s.description,
  updatedAt: s.updatedAt,
  updatedBy: s.updatedBy,
  encrypted: s.encrypted,
});

export const settingsService = {
  async getAll(category?: string): Promise<SystemSetting[]> {
    try {
      const { data } = await adminApi.get<SettingsListResponse>('/admin/settings', {
        params: category ? { category } : undefined,
      });
      return (data?.settings || []).map(toSystemSetting);
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404')) {
        return [];
      }
      throw err;
    }
  },

  async get(key: string): Promise<SystemSetting | null> {
    try {
      const { data } = await adminApi.get<SettingResponse>(`/admin/settings/${key}`);
      return data ? toSystemSetting(data) : null;
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404')) {
        return null;
      }
      throw err;
    }
  },

  async update(
    key: string,
    value: string | number | boolean,
    options?: { encrypted?: boolean; category?: string; description?: string }
  ): Promise<SystemSetting> {
    try {
      const { data } = await adminApi.put<SettingResponse>(`/admin/settings/${key}`, {
        value: String(value),
        encrypted: options?.encrypted || false,
        category: options?.category,
        description: options?.description,
      });
      return toSystemSetting(data);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async delete(key: string): Promise<void> {
    try {
      await adminApi.delete(`/admin/settings/${key}`);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async invalidateCache(): Promise<void> {
    try {
      await adminApi.post('/admin/settings/invalidate-cache');
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async getStripeStatus(): Promise<StripeStatusResponse> {
    try {
      // Try new dedicated route first
      const { data } = await adminApi.get<StripeStatusResponse>('/admin/stripe/config');
      return data || { configured: false, hasSecretKey: false, hasWebhookSecret: false };
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404')) {
        return { configured: false, hasSecretKey: false, hasWebhookSecret: false };
      }
      throw err;
    }
  },

  async updateStripeConfig(config: StripeConfigPayload): Promise<StripeStatusResponse> {
    try {
      const { data } = await adminApi.put<StripeStatusResponse>('/admin/stripe/config', config);
      return data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  // Legacy endpoint for rate limit cache (keep for compatibility)
  async clearRateLimitCache(): Promise<void> {
    try {
      await adminApi.post('/admin/settings/rate-limit/clear-cache');
    } catch (error) {
      // Try new endpoint if old one fails
      try {
        await adminApi.post('/admin/settings/invalidate-cache');
      } catch {
        throw parseApiError(error);
      }
    }
  },
};

export default settingsService;
